import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, computed, inject, signal, viewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { AuthService } from '../auth/auth.service';
import { prefersCompactShell } from '../utils/layout.util';
import { HelpContextService } from './help-context.service';
import {
  HELP_LIMITS,
  HelpService,
  friendlyError,
  isValidEmail,
  technicalDetailsFooter,
  userIdFromToken,
  validateImages,
} from './help.service';

interface Attachment {
  id: number;
  file: File;
  /** Object URL for the preview; revoked when the attachment is removed or the draft is cleared. */
  previewUrl: string;
}

/**
 * Global Help / Report an Issue. Mounted once in each authenticated shell so it opens over whatever screen the
 * user is on: it never navigates, reloads, or touches the screen underneath. The draft lives in this component's
 * memory only (no browser storage), so closing the panel does not unmount or reset the page behind it.
 */
@Component({
  selector: 'app-help',
  imports: [DrawerModule, ButtonModule, InputTextModule, TextareaModule],
  templateUrl: './help.component.html',
  styleUrl: './help.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:paste)': 'onDocumentPaste($event)',
    '(window:resize)': 'onViewportChange()',
  },
})
export class HelpComponent implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly helpApi = inject(HelpService);
  private readonly context = inject(HelpContextService);

  readonly limits = HELP_LIMITS;
  readonly acceptAttr = HELP_LIMITS.acceptedTypes.join(',');

  private readonly launcher = viewChild<ElementRef<HTMLButtonElement>>('launcher');
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  readonly open = signal(false);
  readonly compact = signal(prefersCompactShell());
  /** Pixels the on-screen keyboard covers at the bottom (iOS keeps the layout viewport full height). */
  readonly keyboardInset = signal(0);

  readonly name = signal('');
  readonly email = signal('');
  readonly description = signal('');
  readonly patientName = signal('');
  readonly visitDate = signal('');
  readonly visitId = signal('');
  readonly visitDetails = signal('');
  readonly attachments = signal<Attachment[]>([]);

  readonly showOptional = signal(false);
  readonly showErrors = signal(false);
  readonly dragging = signal(false);
  readonly submitting = signal(false);
  readonly confirmingDiscard = signal(false);
  readonly referenceId = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);
  readonly fileErrors = signal<string[]>([]);

  private nextAttachmentId = 1;
  private prefill = { name: '', email: '', visitId: '', visitDetails: '' };
  private viewportCleanup: (() => void) | null = null;

  readonly position = computed(() => (this.compact() ? 'bottom' : 'right'));
  readonly drawerStyle = computed(() =>
    this.compact()
      ? {
          width: '100%',
          height: 'auto',
          'max-height': '92dvh',
          bottom: `${this.keyboardInset()}px`,
          'border-radius': '1rem 1rem 0 0',
        }
      : { width: 'min(28rem, 100vw)' }
  );

  readonly nameError = computed(() => (this.name().trim() ? null : 'Enter your name.'));
  readonly emailError = computed(() => {
    const v = this.email().trim();
    if (!v) return 'Enter your email address.';
    return isValidEmail(v) ? null : 'Enter a valid email address, like name@example.com.';
  });
  readonly descriptionError = computed(() => {
    const v = this.description().trim();
    if (!v) return 'Describe the issue so we can help.';
    return v.length > HELP_LIMITS.maxDescription
      ? `Please keep the description under ${HELP_LIMITS.maxDescription} characters.`
      : null;
  });
  readonly isValid = computed(() => !this.nameError() && !this.emailError() && !this.descriptionError());

  /** True when the user has typed or attached something worth protecting (prefilled values don't count). */
  readonly isDirty = computed(() => {
    const p = this.prefill;
    return (
      this.description().trim() !== '' ||
      this.attachments().length > 0 ||
      this.patientName().trim() !== '' ||
      this.visitDate() !== '' ||
      this.visitId().trim() !== p.visitId ||
      this.visitDetails().trim() !== p.visitDetails ||
      this.name().trim() !== p.name ||
      this.email().trim() !== p.email
    );
  });

  ngOnDestroy(): void {
    this.viewportCleanup?.();
    this.revokeAll();
  }

  openPanel(): void {
    this.compact.set(prefersCompactShell());
    if (!this.isDirty() && !this.referenceId()) {
      this.applyPrefill();
    }
    this.confirmingDiscard.set(false);
    this.open.set(true);
    this.watchViewport();
  }

  /** Close button, Escape and the Cancel action all come through here. */
  requestClose(): void {
    if (this.submitting()) {
      return;
    }
    if (this.referenceId()) {
      this.finish();
    } else if (this.isDirty()) {
      this.confirmingDiscard.set(true);
      this.focusConfirmAction();
    } else {
      this.hide();
    }
  }

  keepEditing(): void {
    this.confirmingDiscard.set(false);
  }

  discardDraft(): void {
    this.finish();
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    if (this.confirmingDiscard()) {
      this.keepEditing();
    } else {
      this.requestClose();
    }
  }

  onDrawerShown(): void {
    queueMicrotask(() => {
      const root = this.panel()?.nativeElement;
      const target =
        root?.querySelector<HTMLElement>('[data-help-confirm-focus]') ??
        root?.querySelector<HTMLElement>('input:not([type=file]):not([type=hidden]), textarea');
      const firstEmpty = Array.from(
        root?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-help-required]') ?? []
      ).find((el) => !el.value);
      (target?.hasAttribute('data-help-confirm-focus') ? target : firstEmpty ?? target)?.focus();
    });
  }

  onDrawerHidden(): void {
    this.launcher()?.nativeElement.focus();
  }

  // ---- Screenshots -------------------------------------------------------------------------------------------

  onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  openFilePicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  removeAttachment(id: number): void {
    const removed = this.attachments().find((a) => a.id === id);
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.attachments.update((list) => list.filter((a) => a.id !== id));
    this.fileErrors.set([]);
  }

  onDragOver(event: DragEvent): void {
    // Always cancel so a file dropped near (not on) the zone isn't opened by the browser, replacing the app.
    event.preventDefault();
    if (event.dataTransfer?.types.includes('Files')) {
      this.dragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    const related = event.relatedTarget as Node | null;
    if (!related || !(event.currentTarget as HTMLElement).contains(related)) {
      this.dragging.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  /**
   * Ctrl/Cmd+V of a screenshot attaches it even when the description box has focus. Text pastes (including ones
   * that carry an image alongside, like copying from a document) are left alone. Pastes aimed at the screen
   * behind the panel are never intercepted.
   */
  onDocumentPaste(event: ClipboardEvent): void {
    if (!this.open() || this.referenceId()) {
      return;
    }
    const target = event.target as Node | null;
    const insidePanel = !!target && !!this.panel()?.nativeElement.contains(target);
    if (!insidePanel && target !== document.body) {
      return;
    }
    const data = event.clipboardData;
    if (!data) {
      return;
    }
    const images = Array.from(data.items ?? [])
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (images.length === 0 || data.getData('text/plain')) {
      return;
    }
    event.preventDefault();
    this.addFiles(images.map((f, i) => this.nameClipboardImage(f, i)));
  }

  private nameClipboardImage(file: File, index: number): File {
    if (file.name && file.name !== 'image.png') {
      return file;
    }
    const ext = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/webp' ? 'webp' : 'png';
    return new File([file], `pasted-screenshot-${this.nextAttachmentId + index}.${ext}`, { type: file.type });
  }

  private addFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }
    const { accepted, errors } = validateImages(this.attachments().length, files);
    this.fileErrors.set(errors);
    if (accepted.length === 0) {
      return;
    }
    this.attachments.update((list) => [
      ...list,
      ...accepted.map((file) => ({ id: this.nextAttachmentId++, file, previewUrl: URL.createObjectURL(file) })),
    ]);
  }

  // ---- Submit ------------------------------------------------------------------------------------------------

  submit(): void {
    if (this.submitting()) {
      return;
    }
    this.showErrors.set(true);
    if (!this.isValid()) {
      queueMicrotask(() =>
        this.panel()?.nativeElement.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      );
      return;
    }
    this.submitting.set(true);
    this.submitError.set(null);

    const footer = technicalDetailsFooter(
      {
        routeTemplate: this.context.snapshot().routeTemplate,
        userId: userIdFromToken(this.auth.getStoredToken()),
        submittedAt: new Date(),
      },
      typeof navigator === 'undefined' ? '' : navigator.userAgent,
      typeof window === 'undefined' ? 1024 : window.innerWidth
    );

    this.helpApi
      .submit({
        name: this.name(),
        email: this.email(),
        description: this.description().trim() + footer,
        screenshots: this.attachments().map((a) => a.file),
        patientName: this.patientName(),
        visitDate: this.visitDate(),
        visitId: this.visitId(),
        visitDetails: this.visitDetails(),
      })
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          this.referenceId.set(res.referenceId);
          this.focusConfirmAction();
        },
        error: (err: unknown) => {
          // Nothing is cleared on failure: the draft and screenshots stay so the user can retry.
          this.submitting.set(false);
          this.submitError.set(friendlyError(err));
        },
      });
  }

  // ---- Internals ---------------------------------------------------------------------------------------------

  onViewportChange(): void {
    if (this.open()) {
      this.compact.set(prefersCompactShell());
    }
  }

  /** Moves focus to the primary action of the confirmation / success view so keyboard users aren't left behind. */
  private focusConfirmAction(): void {
    setTimeout(() => this.panel()?.nativeElement.querySelector<HTMLElement>('[data-help-confirm-focus]')?.focus());
  }

  private applyPrefill(): void {
    const claims = this.auth.getJwtClaims();
    const ctx = this.context.snapshot();
    const name = claims && claims.name !== claims.email ? claims.name : '';
    this.prefill = {
      name,
      email: claims?.email ?? '',
      visitId: ctx.visitId ?? '',
      // The patient is identified by internal id only; the name is left for the user to add if they want to.
      visitDetails: ctx.patientId ? `Patient ID: ${ctx.patientId}` : '',
    };
    this.name.set(this.prefill.name);
    this.email.set(this.prefill.email);
    this.visitId.set(this.prefill.visitId);
    this.visitDetails.set(this.prefill.visitDetails);
    this.showOptional.set(!!(this.prefill.visitId || this.prefill.visitDetails));
  }

  private finish(): void {
    this.hide();
    this.reset();
  }

  private hide(): void {
    this.open.set(false);
    this.confirmingDiscard.set(false);
    this.viewportCleanup?.();
    this.viewportCleanup = null;
    this.keyboardInset.set(0);
  }

  private reset(): void {
    this.revokeAll();
    this.attachments.set([]);
    this.description.set('');
    this.patientName.set('');
    this.visitDate.set('');
    this.visitId.set('');
    this.visitDetails.set('');
    this.prefill = { name: this.prefill.name, email: this.prefill.email, visitId: '', visitDetails: '' };
    this.showOptional.set(false);
    this.showErrors.set(false);
    this.submitError.set(null);
    this.fileErrors.set([]);
    this.referenceId.set(null);
  }

  private revokeAll(): void {
    for (const a of this.attachments()) {
      URL.revokeObjectURL(a.previewUrl);
    }
  }

  /** Keeps the sheet (and its Submit button) above the on-screen keyboard. */
  private watchViewport(): void {
    this.viewportCleanup?.();
    const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!vv) {
      return;
    }
    const update = () => {
      this.keyboardInset.set(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    this.viewportCleanup = () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }
}
